import { Inject, Injectable } from '@angular/core';
import { CustomCart } from '@cart/const';
import {
  CartActions,
  CartApiService,
  CustomActiveCartService,
  CustomMultiCartFacade,
  MultiCartService,
} from '@cart/data-access';
import { getCartIdByUserId } from '@cart/utils';
import { CityActions } from '@city/data-access';
import { CityService } from '@city/feature';
import { SENTRY, Sentry } from '@core/const';
import { EnvironmentService } from '@core/feature';
import { Actions, EffectNotification, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { UserIdService, normalizeHttpError } from '@spartacus/core';
import { EMPTY, Observable, combineLatest, iif, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CustomMultiCartService } from '../services';

@Injectable()
export class CartBrowserEffects {
  reloadCart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CityActions.SET_ACTIVE_BASE_STORE),
        map(({ payload }) => payload),
        distinctUntilChanged(),
        map(() => {
          combineLatest({ cartId: this.activeCartService.getActiveCartId(), userId: this.userIdService.getUserId() })
            .pipe(
              take(1),
              filter(({ cartId }) => !!cartId),
            )
            .subscribe(({ cartId, userId }) =>
              this.multiCartService.loadCart({ cartId: cartId, userId: userId, extraData: { active: true } }),
            );

          return;
        }),
      ),
    { dispatch: false },
  );

  setActiveCartIdNull$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.SET_ACTIVE_CART_ID),
      filter(({ payload }: CartActions.SetActiveCartId) => payload === null),
      tap(() => {
        const capturedError = new Error('Попытка установки корзины с null значением');
        this.sentry.captureException(capturedError);
      }),
      map(() => new CartActions.SetActiveCartId('')),
    ),
  );

  safelySetActiveCart$: Observable<CartActions.ClearTemporaryCartId> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      debounceTime(0),
      switchMap(() => this.customMultiCartFacade.getTemporaryCartIdValue()),
      filter(Boolean),
      tap((code: string) => {
        this.activeCartService.setActiveCartId(code);
      }),
      map(() => new CartActions.ClearTemporaryCartId()),
    ),
  );

  removeAllCartEntries$: Observable<CartActions.RemoveAllCartEntriesSuccess | CartActions.RemoveAllCartEntriesFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartActions.REMOVE_ALL_CART_ENTRIES),
        map((action: CartActions.RemoveAllCartEntries) => action.payload),
        switchMap(({ userId, cartId }: { userId: string; cartId: string }) =>
          this.occCartService.removeAllEntries(userId, cartId).pipe(
            map(() => new CartActions.RemoveAllCartEntriesSuccess({ userId, cartId })),
            catchError((error) =>
              of(new CartActions.RemoveAllCartEntriesFail({ userId, cartId, error: normalizeHttpError(error) })),
            ),
          ),
        ),
      ),
    );

  refreshCart$: Observable<CartActions.LoadCart> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.REMOVE_ALL_CART_ENTRIES_SUCCESS),
      map((action: CartActions.RemoveAllCartEntriesSuccess) => action.payload),
      map(({ userId, cartId }: { userId: string; cartId: string }) => new CartActions.LoadCart({ userId, cartId })),
    ),
  );

  // следит, чтобы при изменении активной корзины проставлялся ее бэйсстор
  changeActiveCart$: Observable<never> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.CHANGE_ACTIVE_CART),
        map((action: CartActions.ChangeActiveCart) => action.payload),
        switchMap((cartId: string) => this.customMultiCartService.getCartById(cartId).pipe(take(1))),
        withLatestFrom(this.cityService.activeBaseStoreCode$),
        tap(([cart, activeBaseStoreCode]: [CustomCart, string]) => {
          this.activeCartService.setActiveCartId(cart.code);
          if (activeBaseStoreCode !== cart.baseStoreId) {
            this.cityService.setBaseStoreWithoutCompareCart(cart.baseStoreId);
          }
        }),
        switchMap(() => EMPTY),
      ),
    { dispatch: false },
  );

  restoreCart$: Observable<CartActions.RestoreCartFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.RESTORE_CART),
      map((action: CartActions.RestoreCart) => action.payload),
      withLatestFrom(this.userIdService.getUserId()),
      map(([{ cart, active }, userId]) => {
        return { cart, active, userId };
      }),
      map(({ cart, active, userId }: { cart: CustomCart; active: boolean; userId: string }) => {
        const cartGuid = getCartIdByUserId(cart, userId);
        const cartBaseStore = cart.baseStoreId;

        return this.occCartService
          .check(cartGuid, cartBaseStore)
          .pipe(take(1))
          .subscribe((responseCart: CustomCart) => {
            const tempCartId = responseCart.code || responseCart.guid;
            this.customMultiCartFacade.addCartToList({
              cart: responseCart,
              tempCartId,
              userId,
              cartId: getCartIdByUserId(cart, userId),
              active,
            });
          });
      }),
      switchMap(() => EMPTY),
      catchError((error) => of(new CartActions.RestoreCartFail({ error: error }))),
    ),
  );

  constructor(
    private customMultiCartFacade: CustomMultiCartFacade,
    private actions$: Actions,
    private activeCartService: CustomActiveCartService,
    private customMultiCartService: CustomMultiCartService,
    private userIdService: UserIdService,
    private multiCartService: MultiCartService,
    @Inject(SENTRY) private sentry: Sentry,
    private occCartService: CartApiService,
    private cityService: CityService,
    private environmentService: EnvironmentService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(() => this.environmentService.isPlatformBrowser, resolvedEffects$, EMPTY);
  }
}
